import { urls } from 'services/_url';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import SlidingNav from 'app/shared/nav/sliding/sliding';
import { CampaignPreview } from './sections/preview';
import { CampaignOverview } from './sections/overview';
import { ClicksLogs } from './sections/clicks';
import { BounceLogs } from './sections/bounces';
import { RetriesLog } from './sections/retries';
import { useCampaignService } from 'hooks/mailing/campaign';
import { useDispatch, useSelector } from 'react-redux';
import { setPageTitle } from 'store/actions/header';
import { OpenLogs } from './sections/opens';
import ContextMenu from 'app/shared/datatable/context-menu/context-menu';
import { MoreButton } from 'app/shared/button/more';
import { campaignCanBeEdited } from 'utilities/form';
import styles from './view.module.css';
import { addOneCampaignToStore } from 'store/actions/campaign';
import { toast } from 'react-toastify';
import { SendsLogs } from './sections/sends';
import { useSubscriptionService } from 'hooks/sales/subscription';
import useScreenSize from 'hooks/size';
import { Spacer } from 'app/layouts/generic';

const getNavItems = (campaign_id, can_see_logs) => [
  {
    title: 'Overview',
    path: `/campaigns/${campaign_id}/`
  },
  {
    title: 'Details',
    path: `/campaigns/${campaign_id}/details`
  },
  {
    title: 'Delivery log',
    path: `/campaigns/${campaign_id}/deliveries`,
    hidden: !can_see_logs
  },
  {
    title: 'Opens log',
    path: `/campaigns/${campaign_id}/opens`,
    hidden: !can_see_logs
  },
  {
    title: 'Clicks log',
    path: `/campaigns/${campaign_id}/clicks`,
    hidden: !can_see_logs
  },
  {
    title: 'Bounce log',
    path: `/campaigns/${campaign_id}/bounces`,
    hidden: !can_see_logs
  },
  {
    title: 'Retries history',
    path: `/campaigns/${campaign_id}/retries`
  }
];

export const CampaignViewPage = () => {
  const { id } = useParams();
  const { id: user_id, tenant_id, token } = useSelector((state) => state.user_data);
  const { isMobile } = useScreenSize();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fetchCampaign, resendCampaign } = useCampaignService();
  const { fetchSubscription } = useSubscriptionService();

  const [campaign, setCampaign] = useState({});
  const [can_see_logs, setCanSeeLogs] = useState(false);

  useEffect(() => {
    fetchCampaign(id).then(({ campaign }) => {
      setCampaign(() => campaign);
      dispatch(
        setPageTitle([
          { title: 'My Campaigns', path: '/campaigns' },
          { title: campaign.name, path: `/campaigns/${id}` }
        ])
      );
    });
    fetchSubscription({
      query_string: 'plan_code=campaign&status=active'
    }).then(({ subscriptions: [subscription] }) => {
      setCanSeeLogs(subscription && subscription.tier !== 'explorer');
    });
  }, [id]);

  const campaign_actions = [
    [
      { label: 'Edit', value: 'edit' },
      { label: 'Resend to non-openers', value: 'resend' }
    ]
  ];

  const handleAction = (action) => {
    if (action === 'edit') {
      if (!campaignCanBeEdited(campaign)) return toast.error('Cannot edit campaign.');
      if (campaign.is_advanced) {
        window.location.href = `${urls.editor}/campaigns/${campaign.id}?t=${token}&tenant_id=${tenant_id}&user_id=${user_id}`;
        return;
      }
      navigate(`/editor/campaign/${campaign.id}`);
    }

    if (action === 'resend') handleRetryCampaign();
  };

  const handleRetryCampaign = async () => {
    const retry_count = campaign.metadata.retry_count ? campaign.metadata.retry_count + 1 : 1;
    const data = {
      ...campaign,
      metadata: {
        ...campaign.metadata,
        retry_count
      },
      schedule: {
        ...campaign.schedule,
        date: new Date()
      },
      status: 'queued',
      is_enqueued: false
    };

    resendCampaign(campaign, {
      data
    }).then((response) => {
      if (!response) return;
      dispatch(addOneCampaignToStore({ ...data }));
    });
  };

  return (
    <PageContentWrapper>
      <SlidingNav
        nav_items={getNavItems(id, can_see_logs)}
        actions={
          <ContextMenu
            actions={campaign_actions}
            text={<MoreButton text="Actions" className={styles.actions} />}
            callback={handleAction}
            position="right"
          />
        }
      />
      {isMobile && <Spacer multiple={4} />}
      <div className="dt-content-wrapper">
        <Routes>
          <Route path="bounces" element={<BounceLogs />} />
          <Route path="details" element={<CampaignPreview />} />
          <Route path="deliveries" element={<SendsLogs />} />
          <Route path="clicks" element={<ClicksLogs />} />
          <Route path="retries" element={<RetriesLog />} />
          <Route path="opens" element={<OpenLogs />} />
          <Route path="/*" element={<CampaignOverview />} />
        </Routes>
      </div>
    </PageContentWrapper>
  );
};
